'use client'

import {
  Box,
  Flex,
  IconButton,
  Image,
  Link,
  Portal,
  Spinner,
  Text,
} from '@chakra-ui/react'
import { env } from 'next-runtime-env'
import dynamic from 'next/dynamic'
import { useCallback, useEffect, useRef, useState } from 'react'
import {
  FiChevronLeft,
  FiChevronRight,
  FiDownload,
  FiGrid,
  FiHelpCircle,
  FiLink,
  FiMaximize,
  FiMinimize,
  FiPrinter,
  FiShare2,
  FiZoomIn,
  FiZoomOut,
} from 'react-icons/fi'
import { TbArrowsHorizontal } from 'react-icons/tb'
import { useApiContext } from '@/utils/api'
import { isEditableKeyboardTarget } from '@/utils/keyboardEvents'
import { isPdfBackedAttachment } from '@/utils/nativePdfRendererUtils'
import type { Attachment, User } from '@/utils/types'
import { useUser } from '@/utils/userContext'
import AudioViewer from '@/components/Drive/DocumentPreview/AudioViewer'
import ExcelViewer from '@/components/Drive/DocumentPreview/ExcelViewer'
import ImageViewer from '@/components/Drive/DocumentPreview/ImageViewer'
import {
  isAudio,
  isDocx,
  isHtml,
  isImage,
  isPdf,
  isPptx,
  isXls,
  isXlsx,
} from '@/components/Drive/DocumentPreview/index'
import ShareAttachmentModal from '@/components/content/ShareAttachmentModal'
import { CloseButton } from '@/components/ui/CloseButton'
import { toaster } from '@/components/ui/toaster'

const DESKTOP_VIEWER_MIN_WIDTH = 768

const PUBLIC_DOCUMENT_SECTION_SLUGS: Record<string, string> = {
  corporate: 'corporate-presentation',
  reports: 'results',
  regulatory: 'regulatory-news',
  documents: 'documents',
  'fundraising-presentation': 'fundraising-campaign',
  'fundraising-terms': 'fundraising-campaign',
  'fundraising-documents': 'fundraising-campaign',
}

const removeFileExtension = (fileName: string): string => {
  return fileName
    .replace(/\.pdf$/i, '')
    .replace(/\.pptx$/i, '')
    .replace(/\.docx$/i, '')
    .replace(/\.xlsx$/i, '')
    .replace(/\.xls$/i, '')
    .replace(/\.html?$/i, '')
    .replace(/\.m4a$/i, '')
    .replace(/\.mp3$/i, '')
    .replace(/\.wav$/i, '')
    .replace(/\.webm$/i, '')
}

// Dynamically import PDF components to avoid SSR canvas issues
const VercelPDFViewer = dynamic(
  () =>
    import('./VercelPDFViewerIR').catch((error) => {
      console.error('Failed to load PDF viewer:', error)
      // Return a fallback component
      return {
        default: () => (
          <Flex
            justify="center"
            align="center"
            height="100%"
            direction="column"
            gap={4}
          >
            <Text color="white" fontSize="lg">
              Failed to load PDF viewer
            </Text>
            <Text color="gray.400" fontSize="sm">
              Please refresh the page to try again
            </Text>
          </Flex>
        ),
      }
    }),
  {
    ssr: false,
    loading: () => (
      <Flex
        justify="center"
        align="center"
        height="100%"
        direction="column"
        gap={4}
      >
        <Spinner size="xl" color="white" />
        <Text color="white">Loading PDF viewer...</Text>
      </Flex>
    ),
  }
)

const SlideThumbnail = dynamic(
  () => import('@/components/Drive/DocumentPreview/SlideThumbnail'),
  {
    ssr: false,
    loading: () => (
      <Box
        position="relative"
        aspectRatio="16/9"
        bg="gray.700"
        borderRadius="md"
        overflow="hidden"
        mb={1}
        border="1px solid"
        borderColor="gray.600"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <Spinner size="sm" color="gray.400" />
      </Box>
    ),
  }
)

interface InvestorRelationsDesktopDocumentViewerProps {
  attachment: Attachment | null
  isOpen: boolean
  onClose: () => void
  companySlug: string
  section: string
  anonymousMode?: boolean
  preDownloadedBlobUrl?: string | null
  autoPlay?: boolean
  isPreview?: boolean
  organisationId?: string
  companyLogoUrl?: string | null
  assiduousIconUrl?: string
}

const InvestorRelationsDesktopDocumentViewer = ({
  attachment,
  isOpen,
  onClose,
  anonymousMode = false,
  companySlug,
  section,
  preDownloadedBlobUrl = null,
  autoPlay = false,
  isPreview = false,
  organisationId = undefined,
  companyLogoUrl = null,
  assiduousIconUrl = '/images/AssiduousIcon.svg',
}: InvestorRelationsDesktopDocumentViewerProps) => {
  const { api, anonymousApi } = useApiContext()
  const { user } = useUser()
  const [blobUrl, setBlobUrl] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [isFullScreen, setIsFullScreen] = useState(false)
  const [isGridViewActive, setIsGridViewActive] = useState(false)
  const [zoom, setZoom] = useState(100)
  const [fitToScale, setFitToScale] = useState(false)
  const [isPortraitDocument, setIsPortraitDocument] = useState(false)
  const [pageJumpNonce, setPageJumpNonce] = useState(0)
  const NEXT_PUBLIC_ASSIDUOUS_API_URL = env('NEXT_PUBLIC_ASSIDUOUS_API_URL')
  const thumbnailContainerRef = useRef<HTMLDivElement>(null)
  const viewerContainerRef = useRef<HTMLDivElement>(null)
  const htmlPreviewRef = useRef<HTMLIFrameElement>(null)

  const [isNavigatingToPrevPage, setIsNavigatingToPrevPage] = useState(false)
  const [isNavigatingToNextPage, setIsNavigatingToNextPage] = useState(false)
  const hasAppliedDefaultFitRef = useRef(false)

  // Construct secondary logo URL
  let companyLogoURL = companyLogoUrl || ''
  if (isPreview && organisationId) {
    companyLogoURL = `/api/proxy/organisations/${organisationId}/secondary-logo`
  } else if (companySlug) {
    companyLogoURL = `${NEXT_PUBLIC_ASSIDUOUS_API_URL}/investor-relations/${companySlug}/secondary-logo`
  }

  const shareOrganisationId =
    user?.organisation?.orgType === 'client' ? user.organisation.oid : undefined
  const thumbnailRefs = useRef<Record<number, HTMLDivElement | null>>({})
  const scrollPositionRef = useRef(0)
  const isHtmlAttachment = attachment
    ? isHtml(attachment.fileType, attachment.fileName)
    : false
  const isPdfBacked = isPdfBackedAttachment(attachment)

  // Determine if share button should be shown
  // Hide share button for investor users and anonymous users (when user is null)
  const shouldShowShareButton =
    user && !['investor', 'credit_provider'].includes(user.role)

  useEffect(() => {
    const loadAttachment = async () => {
      if (isOpen && attachment) {
        hasAppliedDefaultFitRef.current = false
        setIsGridViewActive(false)
        setIsPortraitDocument(false)
        setPageJumpNonce(0)

        // If in anonymous mode and we have a pre-downloaded blob URL, use it directly
        if (anonymousMode && preDownloadedBlobUrl) {
          setBlobUrl(preDownloadedBlobUrl)

          // Set total pages based on file type
          if (isPdf(attachment.fileType)) {
            setTotalPages(10) // Will be updated by PDF viewer
          } else if (
            isPptx(attachment.fileType) ||
            isDocx(attachment.fileType)
          ) {
            setTotalPages(10) // Will be updated by PDF viewer after conversion
          } else {
            setTotalPages(1)
          }
          return
        }

        // Regular authenticated mode
        setIsLoading(true)
        try {
          let downloadUrl = ''
          let apiInstance = api
          if (isPreview) {
            downloadUrl = `/admin/investor-relations/${organisationId}/documents/${section}/${attachment.attachmentId}`
            apiInstance = api
          } else {
            downloadUrl =
              isPptx(attachment.fileType) || isDocx(attachment.fileType)
                ? `/investor-relations/${companySlug}/documents/${section}/${attachment.attachmentId}?convertToPdf=true`
                : `/investor-relations/${companySlug}/documents/${section}/${attachment.attachmentId}`
            apiInstance = anonymousApi
          }

          const response = await apiInstance.get(downloadUrl, {
            responseType: 'blob',
          })
          const blob = new Blob([response.data], {
            type: response.headers['content-type'] || attachment.fileType,
          })
          const url = URL.createObjectURL(blob)
          setBlobUrl(url)

          // Set total pages based on file type
          if (isPdf(attachment.fileType)) {
            // For PDFs, we'll need to get page count from the PDF viewer
            setTotalPages(10) // Will be updated by PDF viewer
          } else if (
            isPptx(attachment.fileType) ||
            isDocx(attachment.fileType)
          ) {
            setTotalPages(10) // Will be updated by PDF viewer after conversion
          } else {
            setTotalPages(1)
          }
        } catch (error) {
          toaster.create({
            title: 'Error',
            description: 'Failed to load attachment for preview',
            type: 'error',
            duration: 5000,
            closable: true,
          })
          setBlobUrl(null)
        } finally {
          setIsLoading(false)
        }
      } else if (!isOpen) {
        setBlobUrl(null)
        setCurrentPage(1)
        setIsGridViewActive(false)
        setZoom(100)
        setFitToScale(false)
        setIsPortraitDocument(false)
        setPageJumpNonce(0)
        hasAppliedDefaultFitRef.current = false
      }
    }

    loadAttachment()
  }, [
    isOpen,
    attachment,
    api,
    anonymousApi,
    anonymousMode,
    preDownloadedBlobUrl,
    companySlug,
    section,
    organisationId,
    isPreview,
  ])

  useEffect(() => {
    return () => {
      if (blobUrl) {
        URL.revokeObjectURL(blobUrl)
      }
    }
  }, [blobUrl])

  // Scroll to current thumbnail when grid view is opened or page changes
  useEffect(() => {
    if (isGridViewActive && thumbnailRefs.current[currentPage]) {
      const thumbnail = thumbnailRefs.current[currentPage]
      const container = thumbnailContainerRef.current

      if (thumbnail && container) {
        // Calculate the position to scroll to (center the thumbnail)
        const thumbnailTop = thumbnail.offsetTop
        const thumbnailHeight = thumbnail.offsetHeight
        const containerHeight = container.clientHeight
        const scrollTop =
          thumbnailTop - containerHeight / 2 + thumbnailHeight / 2

        container.scrollTo({
          top: Math.max(0, scrollTop),
          behavior: 'smooth',
        })
      }
    }
  }, [isGridViewActive, currentPage])

  const jumpToPage = useCallback(
    (page: number) => {
      const nextPage = Math.max(1, Math.min(page, totalPages))
      setCurrentPage(nextPage)
      setPageJumpNonce((prev) => prev + 1)
    },
    [totalPages]
  )

  const goToNextPage = useCallback(() => {
    setIsNavigatingToPrevPage(false)
    setIsNavigatingToNextPage(true)
    jumpToPage(currentPage + 1)
  }, [currentPage, jumpToPage])

  const goToPrevPage = useCallback(() => {
    setIsNavigatingToNextPage(false)
    setIsNavigatingToPrevPage(true)
    jumpToPage(currentPage - 1)
  }, [currentPage, jumpToPage])

  // Rate-limited click handlers for navigation arrows
  const [lastClickTime, setLastClickTime] = useState(0)
  const MIN_CLICK_INTERVAL = 200 // Increased to 200ms for better stability
  const [isNavigating, setIsNavigating] = useState(false)

  const handlePrevPageClick = useCallback(() => {
    const now = Date.now()
    if (now - lastClickTime >= MIN_CLICK_INTERVAL && !isNavigating) {
      setLastClickTime(now)
      setIsNavigating(true)
      goToPrevPage()
      // Reset navigation state after a shorter delay
      setTimeout(() => setIsNavigating(false), 50)
    }
  }, [lastClickTime, goToPrevPage, isNavigating])

  const handleNextPageClick = useCallback(() => {
    const now = Date.now()
    if (now - lastClickTime >= MIN_CLICK_INTERVAL && !isNavigating) {
      setLastClickTime(now)
      setIsNavigating(true)
      goToNextPage()
      // Reset navigation state after a shorter delay
      setTimeout(() => setIsNavigating(false), 50)
    }
  }, [lastClickTime, goToNextPage, isNavigating])

  const handleTotalPagesChange = useCallback((pages: number) => {
    setTotalPages(pages)
  }, [])

  const handleOrientationDetected = useCallback((isPortrait: boolean) => {
    setIsPortraitDocument((prev) => (prev === isPortrait ? prev : isPortrait))
    if (!hasAppliedDefaultFitRef.current) {
      hasAppliedDefaultFitRef.current = true
      const isDesktopViewer =
        typeof window === 'undefined' ||
        window.innerWidth >= DESKTOP_VIEWER_MIN_WIDTH
      setFitToScale(isPortrait)
      setIsGridViewActive(isPortrait && isDesktopViewer)
    }
  }, [])

  const isPortraitContinuousMode = isPdfBacked && isPortraitDocument

  // Reset navigation direction flags after page change completes
  useEffect(() => {
    if (isNavigatingToPrevPage || isNavigatingToNextPage) {
      // Increase timeout to allow rendering to complete before resetting
      // The scroll effect in VercelPDFViewerIR needs these flags to remain set
      const timer = setTimeout(() => {
        setIsNavigatingToPrevPage(false)
        setIsNavigatingToNextPage(false)
      }, 500) // Allow rendering to complete
      return () => clearTimeout(timer)
    }
  }, [isNavigatingToPrevPage, isNavigatingToNextPage])

  const toggleFullScreen = useCallback(() => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().catch((e) => {
        toaster.create({
          title: 'Error',
          description: 'Error occurred attempting to enable full-screen mode',
          type: 'error',
          duration: 5000,
          closable: true,
        })
      })
    } else {
      document.exitFullscreen()
    }
  }, [])

  const handleDownload = useCallback(() => {
    try {
      if (blobUrl) {
        const link = document.createElement('a')
        link.href = blobUrl
        link.download = attachment?.fileName || 'document'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
      }
    } catch (error) {
      toaster.create({
        title: 'Error',
        description: 'Error downloading attachment',
        type: 'error',
        duration: 5000,
        closable: true,
      })
    }
  }, [blobUrl, attachment])

  const getShortDocumentLink = useCallback(() => {
    if (!attachment?.attachmentId || !companySlug) return null
    const sectionSlug = PUBLIC_DOCUMENT_SECTION_SLUGS[section] || section
    return `${window.location.origin}/investor-relations/${companySlug}/${sectionSlug}/${attachment.attachmentId}`
  }, [attachment?.attachmentId, companySlug, section])

  const handleCopyLink = useCallback(async () => {
    const shortDocumentLink = getShortDocumentLink()
    if (!shortDocumentLink) {
      toaster.create({
        title: 'Unable to copy link',
        description:
          'A public document link is not available for this document.',
        type: 'error',
        duration: 4000,
        closable: true,
      })
      return
    }

    try {
      await navigator.clipboard.writeText(shortDocumentLink)
      toaster.create({
        title: 'Link copied',
        type: 'success',
        duration: 3000,
        closable: true,
      })
    } catch {
      toaster.create({
        title: 'Unable to copy link',
        description: 'Copy permissions are not available in this browser.',
        type: 'error',
        duration: 4000,
        closable: true,
      })
    }
  }, [getShortDocumentLink])

  const toggleGridView = useCallback(() => {
    setIsGridViewActive((prev) => !prev)
  }, [])

  const handleZoomIn = useCallback(() => {
    setFitToScale(false) // Disable fit to scale when manually zooming
    if (
      attachment?.fileType ===
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    ) {
      // For DOCX files, use smaller increments (0.1 = 10%)
      setZoom((prev) => Math.min(prev + 10, 300))
    } else {
      setZoom((prev) => Math.min(prev + 25, 300))
    }
  }, [attachment?.fileType])

  const handleZoomOut = useCallback(() => {
    setFitToScale(false) // Disable fit to scale when manually zooming
    if (
      attachment?.fileType ===
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    ) {
      // For DOCX files, use smaller increments (0.1 = 10%)
      setZoom((prev) => Math.max(prev - 10, 25))
    } else {
      setZoom((prev) => Math.max(prev - 25, 25))
    }
  }, [attachment?.fileType])

  const handleFitToScale = useCallback(() => {
    setFitToScale((prev) => !prev) // Toggle between fit-to-width and default view
    setZoom(100) // Reset zoom when toggling fit-to-width
  }, [])

  const handlePrint = useCallback(() => {
    if (isHtmlAttachment) {
      htmlPreviewRef.current?.contentWindow?.print()
      return
    }

    if (blobUrl) {
      const printWindow = window.open(blobUrl, '_blank')
      if (printWindow) {
        printWindow.onload = () => {
          printWindow.print()
        }
      }
    }
  }, [blobUrl, isHtmlAttachment])

  const handleHelp = useCallback(() => {
    toaster.create({
      title: 'Document Viewer Help',
      description:
        'Use the controls above to navigate, zoom, and interact with your document.',
      type: 'info',
      duration: 5000,
    })
  }, [])

  const handleLogoClick = useCallback(() => {
    // Exit fullscreen if in fullscreen mode
    if (document.fullscreenElement) {
      document.exitFullscreen()
    }

    // Reset any UI state that shouldn't persist
    setIsGridViewActive(false)
    setZoom(100)
    setCurrentPage(1)
    setFitToScale(false)
    setIsPortraitDocument(false)
    setPageJumpNonce(0)
    hasAppliedDefaultFitRef.current = false

    // Close the preview modal
    onClose()
  }, [onClose])

  const handleClose = useCallback(() => {
    const exitPromise = document.fullscreenElement
      ? document.exitFullscreen().catch(() => {})
      : Promise.resolve()
    exitPromise.finally(() => {
      setIsGridViewActive(false)
      setZoom(100)
      setCurrentPage(1)
      setFitToScale(false)
      setIsPortraitDocument(false)
      setPageJumpNonce(0)
      hasAppliedDefaultFitRef.current = false
      onClose()
    })
  }, [onClose])

  // Lock body scroll when viewer is open
  useEffect(() => {
    if (isOpen) {
      const scrollY = window.scrollY
      scrollPositionRef.current = scrollY
      const originalOverflow = document.body.style.overflow
      const originalPosition = document.body.style.position
      const originalTop = document.body.style.top

      document.body.style.overflow = 'hidden'
      document.body.style.position = 'fixed'
      document.body.style.width = '100%'
      document.body.style.height = '100%'
      document.body.style.top = `-${scrollY}px`

      return () => {
        document.body.style.overflow = originalOverflow
        document.body.style.position = originalPosition
        document.body.style.top = originalTop
        document.body.style.width = ''
        document.body.style.height = ''
        window.scrollTo(0, scrollPositionRef.current)
      }
    }
  }, [isOpen])

  useEffect(() => {
    const handleFullScreenChange = () => {
      setIsFullScreen(!!document.fullscreenElement)
    }

    document.addEventListener('fullscreenchange', handleFullScreenChange)

    return () => {
      document.removeEventListener('fullscreenchange', handleFullScreenChange)
    }
  }, [])

  // Handle full screen change via ESC key and keyboard navigation
  useEffect(() => {
    if (!isOpen) {
      return
    }

    const handleFullScreenChange = () => {
      setIsFullScreen(!!document.fullscreenElement)
    }

    // Rate limiting for keyboard navigation
    let lastKeyPressTime = 0
    const MIN_KEY_PRESS_INTERVAL = 200 // Increased to 200ms for better stability

    const handleKeyDown = (event: KeyboardEvent) => {
      const now = Date.now()

      if (event.defaultPrevented || isEditableKeyboardTarget(event.target)) {
        return
      }

      if (event.key === 'ArrowLeft') {
        event.preventDefault()
        if (now - lastKeyPressTime >= MIN_KEY_PRESS_INTERVAL && !isNavigating) {
          lastKeyPressTime = now
          setIsNavigating(true)
          goToPrevPage()
          // Reset navigation state after a shorter delay
          setTimeout(() => setIsNavigating(false), 50)
        }
      } else if (event.key === 'ArrowRight') {
        event.preventDefault()
        if (now - lastKeyPressTime >= MIN_KEY_PRESS_INTERVAL && !isNavigating) {
          lastKeyPressTime = now
          setIsNavigating(true)
          goToNextPage()
          // Reset navigation state after a shorter delay
          setTimeout(() => setIsNavigating(false), 50)
        }
      } else if (event.key === 'Escape') {
        event.preventDefault()
        handleClose()
      }
    }

    // Rate limiting for wheel navigation
    let lastWheelTime = 0
    const MIN_WHEEL_INTERVAL = 200
    let wheelDeltaAccumulator = 0
    const WHEEL_DELTA_THRESHOLD = 50 // Accumulate this much delta before changing page

    const handleWheel = (event: WheelEvent) => {
      if (isPortraitContinuousMode) {
        return
      }

      // Only handle wheel events if there are multiple pages
      if (totalPages <= 1) return

      // Check if the wheel event originated from the thumbnail container
      // If so, let it handle its own scrolling and don't change pages
      const thumbnailContainer = thumbnailContainerRef.current
      if (thumbnailContainer?.contains(event.target as Node)) {
        return // Let the thumbnail container handle scrolling
      }

      const now = Date.now()

      // Check if scrolling vertically (not horizontally)
      if (Math.abs(event.deltaY) > Math.abs(event.deltaX)) {
        // Check if we're zoomed in or fit to width
        const isZoomedOrFit = (zoom && zoom > 100) || fitToScale

        if (isZoomedOrFit) {
          // Find the PDF viewer's scrollable container
          const container = viewerContainerRef.current
          if (!container) return

          // Look for the PDF container within the viewer
          const pdfContainer = container.querySelector(
            '[data-pdf-container]'
          ) as HTMLElement

          if (
            pdfContainer &&
            pdfContainer.scrollHeight > pdfContainer.clientHeight
          ) {
            // Container is scrollable - check if we can scroll within the page
            const scrollTop = pdfContainer.scrollTop
            const scrollHeight = pdfContainer.scrollHeight
            const clientHeight = pdfContainer.clientHeight
            const isAtBottom = scrollTop + clientHeight >= scrollHeight - 5 // 5px threshold
            const isAtTop = scrollTop <= 5 // 5px threshold

            // If scrolling down and not at bottom, allow normal scrolling
            if (event.deltaY > 0 && !isAtBottom) {
              wheelDeltaAccumulator = 0 // Reset accumulator when scrolling normally
              return // Let the browser handle scrolling
            }

            // If scrolling up and not at top, allow normal scrolling
            if (event.deltaY < 0 && !isAtTop) {
              wheelDeltaAccumulator = 0 // Reset accumulator when scrolling normally
              return // Let the browser handle scrolling
            }

            // At boundaries - accumulate delta
            wheelDeltaAccumulator += Math.abs(event.deltaY)

            // Only change page if threshold reached and enough time has passed
            if (
              wheelDeltaAccumulator >= WHEEL_DELTA_THRESHOLD &&
              now - lastWheelTime >= MIN_WHEEL_INTERVAL &&
              !isNavigating
            ) {
              wheelDeltaAccumulator = 0 // Reset accumulator
              lastWheelTime = now
              setIsNavigating(true)
              event.preventDefault()
              event.stopPropagation()

              if (event.deltaY > 0) {
                goToNextPage()
              } else {
                goToPrevPage()
              }

              setTimeout(() => setIsNavigating(false), 50)
            } else {
              // Still accumulating - prevent default to stop page scrolling
              event.preventDefault()
              event.stopPropagation()
            }
          } else {
            // Not scrollable or container not found - accumulate delta
            wheelDeltaAccumulator += Math.abs(event.deltaY)

            if (
              wheelDeltaAccumulator >= WHEEL_DELTA_THRESHOLD &&
              now - lastWheelTime >= MIN_WHEEL_INTERVAL &&
              !isNavigating
            ) {
              wheelDeltaAccumulator = 0
              lastWheelTime = now
              setIsNavigating(true)
              event.preventDefault()
              event.stopPropagation()

              if (event.deltaY > 0) {
                goToNextPage()
              } else {
                goToPrevPage()
              }

              setTimeout(() => setIsNavigating(false), 50)
            } else {
              event.preventDefault()
              event.stopPropagation()
            }
          }
        } else {
          // Not zoomed - accumulate delta
          wheelDeltaAccumulator += Math.abs(event.deltaY)

          if (
            wheelDeltaAccumulator >= WHEEL_DELTA_THRESHOLD &&
            now - lastWheelTime >= MIN_WHEEL_INTERVAL &&
            !isNavigating
          ) {
            wheelDeltaAccumulator = 0
            lastWheelTime = now
            setIsNavigating(true)
            event.preventDefault()
            event.stopPropagation()

            if (event.deltaY > 0) {
              goToNextPage()
            } else {
              goToPrevPage()
            }

            setTimeout(() => setIsNavigating(false), 50)
          } else {
            event.preventDefault()
            event.stopPropagation()
          }
        }
      } else {
        // Horizontal scrolling - reset accumulator
        wheelDeltaAccumulator = 0
      }
    }

    // Touch gesture handling for mobile
    let touchStartX = 0
    let touchStartY = 0
    let touchStartTime = 0
    let lastTouchTime = 0
    const MIN_SWIPE_DISTANCE = 50 // Minimum distance in pixels to trigger swipe
    const MAX_SWIPE_TIME = 300 // Maximum time in ms for a swipe gesture
    const MIN_TOUCH_INTERVAL = 200

    // Pinch-to-zoom state
    let initialPinchDistance = 0
    let initialZoom = 100
    let isPinching = false
    const MIN_PINCH_DISTANCE = 3 // Minimum distance change to trigger zoom (lowered for better mobile sensitivity)

    // Calculate distance between two touches
    const getTouchDistance = (touch1: Touch, touch2: Touch): number => {
      const dx = touch2.clientX - touch1.clientX
      const dy = touch2.clientY - touch1.clientY
      return Math.sqrt(dx * dx + dy * dy)
    }

    const handleTouchStart = (event: TouchEvent) => {
      if (isPortraitContinuousMode) {
        return
      }

      // Handle pinch-to-zoom (2 touches)
      if (event.touches.length === 2) {
        isPinching = true
        const touch1 = event.touches[0]
        const touch2 = event.touches[1]
        initialPinchDistance = getTouchDistance(touch1, touch2)
        initialZoom = zoom
        setFitToScale(false) // Disable fit to scale when pinching
        event.preventDefault()
        event.stopPropagation()
        return
      }

      // Only handle single touch for swipe gestures
      if (event.touches.length !== 1 || totalPages <= 1) return

      const touch = event.touches[0]
      touchStartX = touch.clientX
      touchStartY = touch.clientY
      touchStartTime = Date.now()
    }

    const handleTouchMove = (event: TouchEvent) => {
      if (isPortraitContinuousMode) {
        return
      }

      // Handle pinch-to-zoom (2 touches)
      if (event.touches.length === 2 && isPinching) {
        // Always prevent default on two-touch events to avoid browser interference
        event.preventDefault()
        event.stopPropagation()

        const touch1 = event.touches[0]
        const touch2 = event.touches[1]
        const currentDistance = getTouchDistance(touch1, touch2)

        // Only apply zoom if distance change is significant
        if (
          Math.abs(currentDistance - initialPinchDistance) < MIN_PINCH_DISTANCE
        ) {
          return // Ignore tiny movements but still prevent default above
        }

        // Apply zoom to all file types (including PDFs, PPTX, and DOCX)
        const distanceDelta = currentDistance - initialPinchDistance
        const zoomDelta = (distanceDelta / initialPinchDistance) * 100
        const newZoom = Math.max(25, Math.min(300, initialZoom + zoomDelta))

        setZoom(newZoom)
        setFitToScale(false) // Disable fit to scale when pinching
        return
      }

      // Prevent default scrolling if we're tracking a swipe
      if (event.touches.length === 1 && totalPages > 1 && !isPinching) {
        const touch = event.touches[0]
        const deltaX = Math.abs(touch.clientX - touchStartX)
        const deltaY = Math.abs(touch.clientY - touchStartY)

        // Always prevent default for horizontal swipes
        if (deltaX > deltaY && deltaX > 10) {
          event.preventDefault()
          event.stopPropagation()
          return
        }

        // For vertical swipes, check if zoomed/fit to width
        if (deltaY > deltaX && deltaY > 10) {
          const isZoomedOrFit = (zoom && zoom > 100) || fitToScale

          if (isZoomedOrFit) {
            // Check if we're at scroll boundaries
            const container = viewerContainerRef.current
            if (container) {
              const pdfContainer = container.querySelector(
                '[data-pdf-container]'
              ) as HTMLElement

              if (
                pdfContainer &&
                pdfContainer.scrollHeight > pdfContainer.clientHeight
              ) {
                const scrollTop = pdfContainer.scrollTop
                const scrollHeight = pdfContainer.scrollHeight
                const clientHeight = pdfContainer.clientHeight
                const isAtBottom = scrollTop + clientHeight >= scrollHeight - 5
                const isAtTop = scrollTop <= 5

                // If scrolling down and not at bottom, allow normal scrolling
                if (!isAtBottom) {
                  return // Let the browser handle scrolling
                }

                // If scrolling up and not at top, allow normal scrolling
                if (!isAtTop) {
                  return // Let the browser handle scrolling
                }

                // At boundaries - prevent default and change page
                event.preventDefault()
                event.stopPropagation()

                // Otherwise allow normal scrolling - DO NOTHING, let browser handle it
              } else {
                // PDF container not found or not scrollable - allow normal scrolling
                // Don't prevent default here, let the browser handle it
                // The page change will be handled in handleTouchEnd if it's a valid swipe
              }
            }
            // If container not found, allow normal scrolling
          } else {
            // Not zoomed - prevent default to allow page change
            event.preventDefault()
            event.stopPropagation()
          }
        }
      }
    }

    const handleTouchEnd = (event: TouchEvent) => {
      if (isPortraitContinuousMode) {
        return
      }

      // Reset pinch state when touches end
      if (event.touches.length < 2) {
        isPinching = false
        initialPinchDistance = 0
        initialZoom = 100

        // If we were pinching and now have 1 touch remaining, ignore it to prevent accidental swipes
        if (event.touches.length === 1 && event.changedTouches.length === 1) {
          // Reset touch start positions to prevent swipe detection
          touchStartX = event.touches[0].clientX
          touchStartY = event.touches[0].clientY
          touchStartTime = Date.now()
          return // Exit early to prevent swipe processing
        }
      }

      // Only handle single touch for swipe gestures
      if (event.changedTouches.length !== 1 || totalPages <= 1 || isPinching) {
        return
      }

      const now = Date.now()
      const touch = event.changedTouches[0]
      const deltaX = touch.clientX - touchStartX
      const deltaY = touch.clientY - touchStartY
      const deltaTime = now - touchStartTime

      // Check if it's a valid horizontal swipe gesture
      const isHorizontalSwipe = Math.abs(deltaX) > Math.abs(deltaY)
      const isHorizontalSwipeDistance = Math.abs(deltaX) > MIN_SWIPE_DISTANCE

      // Check if it's a valid vertical swipe gesture
      const isVerticalSwipe = Math.abs(deltaY) > Math.abs(deltaX)
      const isVerticalSwipeDistance = Math.abs(deltaY) > MIN_SWIPE_DISTANCE

      const isSwipeTime = deltaTime < MAX_SWIPE_TIME

      // Handle horizontal swipes
      if (isHorizontalSwipe && isHorizontalSwipeDistance && isSwipeTime) {
        // Rate limiting
        if (now - lastTouchTime >= MIN_TOUCH_INTERVAL && !isNavigating) {
          lastTouchTime = now
          setIsNavigating(true)

          // Swipe right (positive deltaX) = previous page
          // Swipe left (negative deltaX) = next page
          if (deltaX > 0) {
            goToPrevPage()
          } else {
            goToNextPage()
          }

          // Reset navigation state after a shorter delay
          setTimeout(() => setIsNavigating(false), 50)
        }
      }

      // Handle vertical swipes
      if (isVerticalSwipe && isVerticalSwipeDistance && isSwipeTime) {
        const isZoomedOrFit = (zoom && zoom > 100) || fitToScale

        if (isZoomedOrFit) {
          // Check if we're at scroll boundaries before changing page
          const container = viewerContainerRef.current
          if (container) {
            const pdfContainer = container.querySelector(
              '[data-pdf-container]'
            ) as HTMLElement

            if (
              pdfContainer &&
              pdfContainer.scrollHeight > pdfContainer.clientHeight
            ) {
              const scrollTop = pdfContainer.scrollTop
              const scrollHeight = pdfContainer.scrollHeight
              const clientHeight = pdfContainer.clientHeight
              const isAtBottom = scrollTop + clientHeight >= scrollHeight - 5
              const isAtTop = scrollTop <= 5

              // Only change page if at boundaries
              if ((deltaY > 0 && isAtBottom) || (deltaY < 0 && isAtTop)) {
                // Rate limiting
                if (
                  now - lastTouchTime >= MIN_TOUCH_INTERVAL &&
                  !isNavigating
                ) {
                  lastTouchTime = now
                  setIsNavigating(true)

                  // Swipe down (positive deltaY) = next page
                  // Swipe up (negative deltaY) = previous page
                  if (deltaY > 0) {
                    goToNextPage()
                  } else {
                    goToPrevPage()
                  }

                  setTimeout(() => setIsNavigating(false), 50)
                }
              }
              // Otherwise, allow normal scrolling (don't change page)
            } else {
              // Not scrollable - change page directly
              if (now - lastTouchTime >= MIN_TOUCH_INTERVAL && !isNavigating) {
                lastTouchTime = now
                setIsNavigating(true)

                if (deltaY > 0) {
                  goToNextPage()
                } else {
                  goToPrevPage()
                }

                setTimeout(() => setIsNavigating(false), 50)
              }
            }
          } else {
            // Container not found - fallback to direct page change
            if (now - lastTouchTime >= MIN_TOUCH_INTERVAL && !isNavigating) {
              lastTouchTime = now
              setIsNavigating(true)

              if (deltaY > 0) {
                goToNextPage()
              } else {
                goToPrevPage()
              }

              setTimeout(() => setIsNavigating(false), 50)
            }
          }
        } else {
          // Not zoomed - change page directly
          if (now - lastTouchTime >= MIN_TOUCH_INTERVAL && !isNavigating) {
            lastTouchTime = now
            setIsNavigating(true)

            // Swipe down (positive deltaY) = next page
            // Swipe up (negative deltaY) = previous page
            if (deltaY > 0) {
              goToNextPage()
            } else {
              goToPrevPage()
            }

            setTimeout(() => setIsNavigating(false), 50)
          }
        }
      }
    }

    const handleTouchCancel = (event: TouchEvent) => {
      if (isPortraitContinuousMode) {
        return
      }

      // Reset all touch state when touch is cancelled
      isPinching = false
      initialPinchDistance = 0
      initialZoom = 100
      touchStartX = 0
      touchStartY = 0
      touchStartTime = 0
    }

    const container = viewerContainerRef.current

    // Attach keyboard and fullscreen listeners to document (global)
    document.addEventListener('fullscreenchange', handleFullScreenChange)
    document.addEventListener('keydown', handleKeyDown)

    // Attach wheel and touch listeners to the container (works anywhere in viewer)
    if (container) {
      container.addEventListener('wheel', handleWheel, { passive: false })
      container.addEventListener('touchstart', handleTouchStart, {
        passive: false,
      })
      container.addEventListener('touchmove', handleTouchMove, {
        passive: false,
      })
      container.addEventListener('touchend', handleTouchEnd, { passive: true })
      container.addEventListener('touchcancel', handleTouchCancel, {
        passive: true,
      })
    }

    return () => {
      document.removeEventListener('fullscreenchange', handleFullScreenChange)
      document.removeEventListener('keydown', handleKeyDown)
      if (container) {
        container.removeEventListener('wheel', handleWheel)
        container.removeEventListener('touchstart', handleTouchStart)
        container.removeEventListener('touchmove', handleTouchMove)
        container.removeEventListener('touchend', handleTouchEnd)
        container.removeEventListener('touchcancel', handleTouchCancel)
      }
    }
  }, [
    goToPrevPage,
    goToNextPage,
    isNavigating,
    handleClose,
    totalPages,
    zoom,
    fitToScale,
    isPortraitContinuousMode,
    isOpen,
  ])

  if (!isOpen) return null

  // If this is an audio file, render the AudioViewer instead
  if (attachment && isAudio(attachment.fileType) && blobUrl && !isLoading) {
    return (
      <AudioViewer
        fileUrl={blobUrl}
        fileName={attachment.fileName}
        attachmentId={attachment.attachmentId}
        onClose={handleClose}
        anonymousMode={anonymousMode}
        subheading={attachment.subheading}
        autoPlay={autoPlay}
      />
    )
  }

  const renderPreview = () => {
    if (isLoading) {
      return (
        <Flex
          justify="center"
          align="center"
          height="100%"
          direction="column"
          gap={4}
        >
          <Spinner size="xl" color="white" />
          <Text color="white">Loading document...</Text>
        </Flex>
      )
    }

    if (!blobUrl) {
      return (
        <Flex justify="center" align="center" height="100%">
          <Text color="white">
            No document to display or an error occurred.
          </Text>
        </Flex>
      )
    }

    try {
      if (attachment && isImage(attachment.fileType)) {
        return <ImageViewer fileUrl={blobUrl} fileName={attachment.fileName} />
      }

      if (attachment && isPdf(attachment.fileType)) {
        return (
          <VercelPDFViewer
            fileUrl={blobUrl}
            defaultZoom={1.0}
            currentPage={currentPage}
            onPageChange={setCurrentPage}
            onTotalPagesChange={handleTotalPagesChange}
            zoom={zoom}
            fitToScale={fitToScale}
            scrollToBottom={isNavigatingToPrevPage}
            scrollToTop={isNavigatingToNextPage}
            continuousScroll={isPortraitContinuousMode}
            onOrientationDetected={handleOrientationDetected}
            pageJumpTarget={currentPage}
            pageJumpNonce={pageJumpNonce}
          />
        )
      }

      if (attachment && isHtmlAttachment) {
        return (
          <Box w="100%" h="100%" bg="white">
            <iframe
              ref={htmlPreviewRef}
              src={blobUrl}
              title={attachment.fileName || 'HTML document preview'}
              sandbox="allow-downloads allow-forms allow-modals allow-scripts"
              style={{
                width: '100%',
                height: '100%',
                border: 'none',
                backgroundColor: 'white',
              }}
            />
          </Box>
        )
      }

      if (attachment && isDocx(attachment.fileType)) {
        // DOCX files are automatically converted to PDF before being shown
        return (
          <VercelPDFViewer
            fileUrl={blobUrl}
            defaultZoom={1.0}
            currentPage={currentPage}
            onPageChange={setCurrentPage}
            onTotalPagesChange={handleTotalPagesChange}
            zoom={zoom}
            fitToScale={fitToScale}
            scrollToBottom={isNavigatingToPrevPage}
            scrollToTop={isNavigatingToNextPage}
            continuousScroll={isPortraitContinuousMode}
            onOrientationDetected={handleOrientationDetected}
            pageJumpTarget={currentPage}
            pageJumpNonce={pageJumpNonce}
          />
        )
      }

      if (attachment && isXlsx(attachment.fileType)) {
        // For Excel files, render in original format without Vercel wrapper
        return (
          <Box
            position="fixed"
            top={0}
            left={0}
            right={0}
            bottom={0}
            zIndex={9999}
            bg="white"
            display="flex"
            flexDirection="column"
          >
            {/* Simple header with close button */}
            <Flex
              alignItems="center"
              justifyContent="space-between"
              h="48px"
              px={4}
              bg="gray.50"
              borderBottom="1px"
              borderColor="gray.200"
            >
              <Text fontSize="sm" fontWeight="medium" color="gray.700">
                {removeFileExtension(attachment.fileName)}
              </Text>
              <CloseButton
                label="Close"
                onClose={handleClose}
                position="static"
              />
            </Flex>

            {/* Excel content in original format */}
            <Box flex={1} overflow="hidden">
              <ExcelViewer fileUrl={blobUrl} />
            </Box>
          </Box>
        )
      }

      if (attachment && isPptx(attachment.fileType)) {
        return (
          <VercelPDFViewer
            fileUrl={blobUrl}
            defaultZoom={1.0}
            currentPage={currentPage}
            onPageChange={setCurrentPage}
            onTotalPagesChange={handleTotalPagesChange}
            zoom={zoom}
            fitToScale={fitToScale}
            scrollToBottom={isNavigatingToPrevPage}
            scrollToTop={isNavigatingToNextPage}
            continuousScroll={isPortraitContinuousMode}
            onOrientationDetected={handleOrientationDetected}
            pageJumpTarget={currentPage}
            pageJumpNonce={pageJumpNonce}
          />
        )
      }
    } catch (error) {
      toaster.create({
        title: 'Error',
        description: 'Error rendering document',
        type: 'error',
        duration: 5000,
        closable: true,
      })
      return (
        <Flex justify="center" align="center" height="100%">
          <Text color="white">Error rendering document.</Text>
        </Flex>
      )
    }

    return (
      <Flex justify="center" align="center" height="100%">
        <Text color="white">Preview for this file type is not supported.</Text>
      </Flex>
    )
  }

  return (
    <>
      <Portal>
        <Box
          ref={viewerContainerRef}
          position="fixed"
          top={0}
          left={0}
          right={0}
          bottom={0}
          zIndex={9999}
          bg="gray.900"
          display="flex"
          flexDirection="column"
          overflow="hidden"
        >
          {/* Top Header Bar */}
          <Flex
            alignItems={{ base: 'flex-start', md: 'center' }}
            justifyContent={{ base: 'center', md: 'space-between' }}
            flexDirection={{ base: 'column', md: 'row' }}
            h={{ base: 'auto', md: '64px' }}
            px={6}
            py={{ base: 3, md: 0 }}
            bg="gray.900"
            borderBottom="1px"
            borderColor="gray.800"
            flexShrink={0}
            gap={{ base: 2, md: 0 }}
          >
            {/* Left: Company Logo */}
            {companyLogoURL && (
              <Box
                h={{ base: '40px', md: '48px' }}
                w={{ base: '160px', md: '250px' }}
                justifyContent={{ base: 'center', md: 'flex-start' }}
                alignSelf={{ base: 'center', md: 'auto' }}
                display="flex"
                alignItems="center"
                overflow="hidden"
                onClick={handleLogoClick}
                cursor="pointer"
              >
                <Image
                  src={companyLogoURL}
                  objectFit="cover"
                  loading="eager"
                  fetchPriority="high"
                />
              </Box>
            )}
            {/* Right: Page Number and Action Icons */}
            <Flex
              alignItems={{ base: 'center', md: 'center' }}
              justifyContent={{ base: 'center', md: 'flex-end' }}
              flexDirection={{ base: 'column', md: 'row' }}
              gap={{ base: 1.5, md: 4 }}
              marginLeft="auto"
              w="full"
              maxW="full"
              textAlign={{ base: 'center', md: 'left' }}
            >
              <Text
                color="white"
                fontSize={{ base: 'sm', md: 'md' }}
                fontWeight="medium"
                display={isHtmlAttachment ? 'none' : 'flex'}
                alignItems="center"
                lineHeight="1"
              >
                {currentPage} / {totalPages}
              </Text>
              <Flex
                alignItems="center"
                justifyContent={{ base: 'center', md: 'flex-start' }}
                gap={{ base: 1.5, md: 2 }}
                overflowX={{ base: 'auto', md: 'visible' }}
                w={{ base: 'full', md: 'auto' }}
                pb={{ base: 1, md: 0 }}
                whiteSpace="nowrap"
              >
                {!isHtmlAttachment && (
                  <IconButton
                    variant="ghost"
                    size="sm"
                    onClick={toggleGridView}
                    color={isGridViewActive ? 'white' : 'gray.400'}
                    _hover={{ bg: 'gray.700', color: 'white' }}
                    aria-label="Toggle slide thumbnails view"
                    title="Toggle slide thumbnails view"
                  >
                    <FiGrid size={20} />
                  </IconButton>
                )}
                <IconButton
                  variant="ghost"
                  size="sm"
                  onClick={toggleFullScreen}
                  color="gray.400"
                  _hover={{ bg: 'gray.700', color: 'white' }}
                  aria-label={
                    isFullScreen ? 'Exit fullscreen' : 'Enter fullscreen'
                  }
                  title={isFullScreen ? 'Exit fullscreen' : 'Enter fullscreen'}
                >
                  {isFullScreen ? (
                    <FiMinimize size={20} />
                  ) : (
                    <FiMaximize size={20} />
                  )}
                </IconButton>
                {!isHtmlAttachment && (
                  <IconButton
                    variant="ghost"
                    size="sm"
                    onClick={handleFitToScale}
                    color={fitToScale ? 'white' : 'gray.400'}
                    _hover={{ bg: 'gray.700', color: 'white' }}
                    aria-label="Fit to width"
                    title="Fit to width"
                  >
                    <TbArrowsHorizontal size={20} />
                  </IconButton>
                )}
                {!isHtmlAttachment && (
                  <IconButton
                    variant="ghost"
                    size="sm"
                    onClick={handleZoomIn}
                    color="gray.400"
                    _hover={{ bg: 'gray.700', color: 'white' }}
                    aria-label="Zoom in"
                    title="Zoom in"
                  >
                    <FiZoomIn size={20} />
                  </IconButton>
                )}
                {!isHtmlAttachment && (
                  <IconButton
                    variant="ghost"
                    size="sm"
                    onClick={handleZoomOut}
                    color="gray.400"
                    _hover={{ bg: 'gray.700', color: 'white' }}
                    aria-label="Zoom out"
                    title="Zoom out"
                  >
                    <FiZoomOut size={20} />
                  </IconButton>
                )}
                <IconButton
                  variant="ghost"
                  size="sm"
                  onClick={handleDownload}
                  color="gray.400"
                  _hover={{ bg: 'gray.700', color: 'white' }}
                  aria-label="Download document"
                  title="Download document"
                >
                  <FiDownload size={20} />
                </IconButton>
                <IconButton
                  variant="ghost"
                  size="sm"
                  onClick={handleCopyLink}
                  color="gray.400"
                  _hover={{ bg: 'gray.700', color: 'white' }}
                  aria-label="Copy document link"
                  title="Copy document link"
                >
                  <FiLink size={20} />
                </IconButton>
                <IconButton
                  variant="ghost"
                  size="sm"
                  onClick={handlePrint}
                  color="gray.400"
                  _hover={{ bg: 'gray.700', color: 'white' }}
                  aria-label="Print document"
                  title="Print document"
                >
                  <FiPrinter size={20} />
                </IconButton>
                {shouldShowShareButton && !anonymousMode && (
                  <ShareAttachmentModal
                    attachmentId={attachment?.attachmentId || ''}
                    organisationId={shareOrganisationId}
                    title="Share Advisory"
                    description="Share this advisory with other users via email"
                    submitLabel="Share Advisory"
                    successDescription="Advisory shared successfully"
                  >
                    <IconButton
                      variant="ghost"
                      size="sm"
                      color="gray.400"
                      _hover={{ bg: 'gray.700', color: 'white' }}
                      _active={{ bg: 'gray.600', color: 'white' }}
                      aria-label="Share document"
                      title="Share document"
                      zIndex={1}
                    >
                      <FiShare2 size={20} />
                    </IconButton>
                  </ShareAttachmentModal>
                )}
                <IconButton
                  variant="ghost"
                  size="sm"
                  onClick={handleHelp}
                  color="gray.400"
                  _hover={{ bg: 'gray.700', color: 'white' }}
                  aria-label="Help and support"
                  title="Help and support"
                >
                  <FiHelpCircle size={20} />
                </IconButton>
                <CloseButton
                  label="Close viewer"
                  onClose={handleClose}
                  appearance="overlay"
                  position="static"
                  color="gray.400"
                  _hover={{ color: 'gray.100' }}
                  title="Close viewer (Esc)"
                />
              </Flex>
            </Flex>
          </Flex>

          <Flex flex={1} overflow="hidden">
            {/* Grid View Sidebar */}
            {isGridViewActive && !isHtmlAttachment && (
              <Box
                w="300px"
                bg="gray.800"
                borderRight="1px"
                borderColor="gray.700"
                flexShrink={0}
                display="flex"
                flexDirection="column"
                overflow="hidden"
              >
                <Box
                  ref={thumbnailContainerRef}
                  flex={1}
                  overflowY="auto"
                  overflowX="hidden"
                  px={4}
                  pt="16px"
                  pb={4}
                  css={{
                    '&::-webkit-scrollbar': {
                      width: '8px',
                    },
                    '&::-webkit-scrollbar-track': {
                      background: '#374151',
                      borderRadius: '4px',
                    },
                    '&::-webkit-scrollbar-thumb': {
                      background: '#6B7280',
                      borderRadius: '4px',
                    },
                    '&::-webkit-scrollbar-thumb:hover': {
                      background: '#9CA3AF',
                    },
                    '&::-webkit-scrollbar-corner': {
                      background: '#374151',
                    },
                  }}
                >
                  <Flex direction="column" gap={3}>
                    {blobUrl &&
                      Array.from({ length: totalPages }, (_, index) => (
                        <Box
                          // biome-ignore lint/suspicious/noArrayIndexKey: TODO
                          key={index}
                          ref={(el) => {
                            thumbnailRefs.current[index + 1] = el
                          }}
                        >
                          <SlideThumbnail
                            fileUrl={blobUrl}
                            pageNumber={index + 1}
                            isActive={currentPage === index + 1}
                            onClick={() => jumpToPage(index + 1)}
                          />
                        </Box>
                      ))}
                  </Flex>
                </Box>
              </Box>
            )}

            {/* Main Content Area */}
            <Box flex={1} bg="gray.800" position="relative">
              {/* Document Content */}
              <Box
                width="100%"
                height="100%"
                position="absolute"
                top={0}
                left={0}
                transform={
                  attachment &&
                  (isPdf(attachment.fileType) ||
                    isHtmlAttachment ||
                    isPptx(attachment.fileType) ||
                    isDocx(attachment.fileType))
                    ? 'none'
                    : `scale(${zoom / 100})`
                }
                transformOrigin="center center"
                transition="transform 0.2s"
              >
                {renderPreview()}
              </Box>

              {/* Navigation Arrows */}
              {totalPages > 1 &&
                !isHtmlAttachment &&
                !isPortraitContinuousMode && (
                  <>
                    {currentPage > 1 && (
                      <IconButton
                        variant="ghost"
                        size="lg"
                        position="absolute"
                        left={4}
                        top="50%"
                        transform="translateY(-50%)"
                        w="64px"
                        h="64px"
                        borderRadius="full"
                        bg="gray.700"
                        opacity={isNavigating ? 0.3 : 0.5}
                        _hover={{ bg: 'gray.700' }}
                        onClick={handlePrevPageClick}
                        disabled={isNavigating}
                        cursor={isNavigating ? 'not-allowed' : 'pointer'}
                        aria-label="Previous page"
                        title="Previous page"
                        color="white"
                        zIndex={10}
                        pointerEvents="auto"
                      >
                        <FiChevronLeft size={40} />
                      </IconButton>
                    )}
                    {currentPage < totalPages && (
                      <IconButton
                        variant="ghost"
                        size="lg"
                        position="absolute"
                        right={4}
                        top="50%"
                        transform="translateY(-50%)"
                        w="64px"
                        h="64px"
                        borderRadius="full"
                        bg="gray.700"
                        opacity={isNavigating ? 0.3 : 0.5}
                        _hover={{ bg: 'gray.700' }}
                        onClick={handleNextPageClick}
                        disabled={isNavigating}
                        cursor={isNavigating ? 'not-allowed' : 'pointer'}
                        aria-label="Next page"
                        title="Next page"
                        color="white"
                        zIndex={10}
                        pointerEvents="auto"
                      >
                        <FiChevronRight size={40} />
                      </IconButton>
                    )}
                  </>
                )}
            </Box>
          </Flex>

          {/* Bottom Footer Bar */}
          <Flex
            alignItems="center"
            justifyContent="space-between"
            h="48px"
            px={6}
            bg="gray.900"
            borderTop="1px"
            borderColor="gray.800"
            flexShrink={0}
          >
            <Flex align="center" gap={2} color="gray.400">
              <Text as="span" fontSize="sm">
                Powered by
              </Text>
              <Image
                src={assiduousIconUrl}
                alt="Assiduous"
                boxSize="18px"
                objectFit="contain"
              />
            </Flex>
          </Flex>
        </Box>
      </Portal>
    </>
  )
}

export default InvestorRelationsDesktopDocumentViewer
